import type { FC, ReactNode } from 'react';

import { CodeChip } from '../../../components/reusable/code-chip';
import { CardItems } from '../../../components/reusable/components';
import { splitElectoralCode } from '../../../components/reusable/helper';
import { useReduxContext } from '../../../context/redux';
import type { ILGASummary } from './utils';

interface IDdRow {
	title: string;
	value: string | number;
	copy?: boolean;
	action?: () => void;
	renderValue?: ReactNode;
}

export const LGASummary: FC<ILGASummary> = ({ lgaData, rightPanel }) => {
	const metaData = lgaData?.data;
	if (!metaData) return <></>;
	const parts = splitElectoralCode(metaData.lgaCode);
	const stateName = metaData.state?.name ?? '';
	const ddata: IDdRow[] = metaData
		? [
				{
					title: 'State Name/Code',
					value: `${stateName} (${parts.state ?? ''})`.trim(),
					renderValue: (
						<span className='f-row-7 align-items-center gap-7'>
							{stateName ? <span>{stateName}</span> : null}
							{parts.state ? (
								<CodeChip
									code={parts.state}
									level='state'
									compact
								/>
							) : null}
						</span>
					),
					action: metaData.state?.id
						? () => {
								rightPanel?.callSection({
									action: 'view',
									component: 'state-details',
									title: stateName || 'State',
									data: {
										id: metaData.state.id,
									},
								});
							}
						: undefined,
				},
				{
					title: 'Total No. of Constituencies',
					value: metaData.constituencyCount,
					action: () => {
						rightPanel?.callSection({
							action: 'view',
							component: 'ed-in-lga',
							title: `Constituencies for ${metaData.name} LGA`,
							data: {
								id: metaData.id,
								electoralDivision: 'constituency',
							},
						});
					},
				},
				{
					title: 'LGA Name',
					value: metaData.name,
					copy: true,
				},

				{
					title: 'LGA Code',
					value: parts.lga ?? '',
					renderValue: (
						<CodeChip
							code={parts.lga}
							level='lga'
							peek={false}
						/>
					),
				},
				{
					title: 'No. of RA/Ward',
					value: metaData.wardCount,
					action: () => {
						rightPanel?.callSection({
							action: 'view',
							component: 'ed-in-lga',
							title: `RA/Wards in ${metaData.name} Local Government`,
							data: {
								id: metaData.id,
								electoralDivision: 'ward',
							},
						});
					},
				},
				{
					title: 'Total Polling Units',
					value: metaData.pollingUnitCount,
					action: () => {
						rightPanel?.callSection({
							action: 'view',
							component: 'ed-in-lga',
							title: `Polling Units in ${metaData.name} Local Government`,
							data: {
								id: metaData.id,
								electoralDivision: 'pollingunit',
							},
						});
					},
				},
			]
		: [];
	const data = metaData
		? [
				{
					title: 'Description',
					value: metaData.description || '',
					details: [],
				},
			]
		: [];

	return (
		<div className='f-column-33 text-left pt-3'>
			<div className='grid-wrapper-40 gap-33 text-left'>
				{ddata.map((i, index) => (
					<div key={index}>
						<CardItems
							title={i.title}
							value={i?.value?.toLocaleString() || ''}
							copy={i.copy}
							onHandleClick={i.action}
							renderValue={i.renderValue}
						/>
					</div>
				))}
			</div>
			<div className='f-column-33 text-left'>
				{data.map((i, index) => (
					<div key={index}>
						<CardItems
							title={i.title}
							value={i.value.toLocaleString()}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

export const LGADescription = () => {
	const { states } = useReduxContext();
	const metaData = states?.lga_?.lga_getById?.data;
	if (!metaData) return <></>;

	return <p className='color-label text-little'>{metaData.description}</p>;
};
